import { capitalizeString } from 'helpers/Utils.js';

export function ListStoreDefault(defaultFilters, options = {}) {
  let shouldFetch;

  if (typeof options.shouldFetch !== 'undefined') {
    shouldFetch = options.shouldFetch;
  } else {
    shouldFetch = true;
  }

  return {
    data: [],
    pagination: {
      current: null,
      max: null,
    },
    interval: null,
    status: null,
    fetching: false,
    fetched: false,
    shouldFetch,
    clear: true,
    filters: defaultFilters,
  };
}

// handles adding / removing filters for component sorting.
export const ListFilterHandler = (state, action, section) => {
  const filters = action.filters || action.sortFilters;
  let newFilters;
  let clear = false;

  if (Object.keys(filters).length > 0) {
    newFilters = Object.assign({}, state[section].filters, filters);
    clear = clearFilters(state[section].filters, newFilters);
  } else {
    newFilters = {};
    clear = true;
  }

  const sectionData = Object.assign({}, state[section], { filters: newFilters, shouldFetch: true, clear });
  return Object.assign({}, state, { [section]: sectionData });
};

// clear sortBy, sortOrder, and page of a table filter
function clearFilters(oldFilters, newFilters) {
  let clear = false;
  // sort update
  const sortUpdate = oldFilters.sortOrder !== newFilters.sortOrder || oldFilters.sortBy !== newFilters.sortBy;
  // page update
  const pageReset = newFilters.pageNum === 1;

  if (pageReset || sortUpdate) {
    clear = true;
  }

  return clear;
}

// updates the state when the request for new / more content is made
export function ListRequestHandler(state, action, type, clearData = []) {
  const update = { fetching: true, fetched: false, shouldFetch: false };
  // clear out the current data
  if (action.clear) update.data = clearData;

  const sectionData = Object.assign({}, state[type], update);
  return Object.assign({}, state, { [type]: sectionData });
}

export function ListSuccessHandler(state, action, section) {
  const { data, clear, pagination, interval, status } = action;
  const newData = clear ? [...data] : [...state[section].data, ...data];

  const sectionData = Object.assign({}, state[section], {
    data: newData,
    pagination: pagination || {},
    status,
    interval,
    fetching: false,
    fetched: true,
    shouldFetch: false,
    sortFilters: state[section].sortFilters,
  });

  return Object.assign({}, state, { [section]: sectionData });
}

// Generates default store structure
export function storeFilled(action, options = { }) {
  const { fallback, filters } = options;

  return {
    data: action.data || fallback,
    filters,
    status: action.status,
    fetched: true,
    fetching: false,
  };
}

// Generates default store structure
export function storeDefault(defaultDataState, options = {}) {
  const { filters, table } = options;
  let store = {
    data: defaultDataState,
    status: null,
    fetched: false,
    fetching: false,
    shouldFetch: true,
  };

  if (table) {
    store = Object.assign({}, store, {
      pagination: {},
      interval: null,
      shouldFetch: true,
      clear: true,
      filters: {},
    });
  }

  if (filters) {
    store.filters = filters;
  }

  return store;
}

export function storeRequest(fetchingDataState) {
  return {
    data: fetchingDataState,
    status: 'fetching',
    fetched: false,
    fetching: true,
  };
}

export const batchStoreFetchingHandler = (state, stores, clear) => {
  const newState = { ...state };
  stores.forEach((store) => {
    newState[store] = { ...state[store], ...{ shouldFetch: true, clear } };
  });

  return newState;
};

export const pageFilterHandler = (state, action, stores) => {
  const newState = {
    ...batchStoreFetchingHandler(state, stores),
    ...{
      pageFilters: {
        ...state.pageFilters,
        ...action.filters,
      },
    },
  };

  return newState;
};

export function locationChangePersist(state, action, defaultState, { sections, pathToMatch, persistEverything }) {
  const { pathname } = action.payload.location;

  if (pathname && new RegExp(pathToMatch).test(pathname)) {
    if (persistEverything) {
      return Object.assign({}, state);
    }
    return Object.assign({}, defaultState, sections.reduce((object, section) => ({ ...object, [section]: state[section] }), {}));
  }
  return Object.assign({}, defaultState);
}

export const removeSingleNestedStore = (state, removeKey) => {
  const newState = {};
  for (const key in state) {
    if (state.hasOwnProperty(key) && key !== removeKey) {
      newState[key] = state[key];
    }
  }
  return { ...newState };
};

export const fetchErrorNestedStores = (state, section) => {
  const newState = { ...state };

  const stores = Object.keys(state).filter(key => key === section);
  stores.forEach((store) => {
    Object.keys(newState[store]).forEach(nestedStore => {
      newState[store][nestedStore] = {
        ...newState[store][nestedStore],
        ...{ fetched: true, fetching: false, status: 'error', shouldFetch: false },
      };
    });
  });

  return newState;
};

export const RichEntityDataListHandler = (state, action) => {
  const { value, entityType } = action.data;
  const richDataListKey = `rich${capitalizeString(entityType)}Data`;
  const entityListKey = `${entityType}sList`;
  const entityIdKey = `${entityType}_ids`;

  const entityList = state[entityListKey].data || [];
  let newRichDataList = [...state[richDataListKey]];

  // first we need to remove anything no longer in the value (publisher_ids/brand_ids) list
  newRichDataList = newRichDataList.filter(({ public_id }) => value.includes(public_id));
  // now we add any new entities
  value.forEach(id => {
    if (!newRichDataList.find(({ public_id }) => public_id === id)) {
      newRichDataList.push(entityList.find(({ public_id }) => public_id === id));
    }
  });

  const newState = {
    ...state,
    [richDataListKey]: newRichDataList,
    [entityIdKey]: value,
  };
  return newState;
};

export const setPartnershipMetadata = (state, action) => {
  const entityType = action.entityType;
  const nestedEntityType = entityType === 'brand' ? 'publisher' : 'brand';
  const fetchedData = {
    status: action.status,
    fetched: true,
    fetching: false,
  };
  const entityMetadata = {
    data: action.data[entityType],
    ...fetchedData,
  };
  const partnerMetadata = {
    data: action.data[nestedEntityType],
    ...fetchedData,
  };
  return { ...state, metadata: entityMetadata, partner: partnerMetadata };
};

function setEntityNestedBoolState(state, action, key, status) {
  if (action.status === 'success') {
    const newMetadata = {
      ...state.metadata,
      data: { ...state.metadata.data, [key]: status },
    };
    return { ...state, metadata: newMetadata };
  }

  return state;
}

export const setCompetitorState = (state, action, status) => setEntityNestedBoolState(state, action, 'is_competitor', status);
export const setSubscriptionState = (state, action, status) => setEntityNestedBoolState(state, action, 'is_subscribed', status);
